import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    CircularProgress,
    Grid,
    withStyles,
    Hidden,
    Typography,
} from '@material-ui/core';
import AdminContainer from '../../common/AdminContainer';
import styles from './styles';
import { connect, useSelector } from 'react-redux';
import { fetchOwnerDashboardRequested } from '../../stores/dashboard/actions';
import PropertySelector from './PropertySelector';
import OwnerDashboardCards from './OwnerDashboardCards';
import OwnerDashboardChartView from './OwnerDashboardChartView';
import DateRangeDialog from './DateRangeDialog';
import ExportChartCSVButton from './ExportChartCSVButton';
import {
    getOwnerDashboardLoading,
    getOwnerDashboardStartDate,
    getOwnerDashboardEndDate,
    getOwnerDashboardError,
    getOwnersProperties,
} from '../../stores/dashboard/selectors';
import { DateTime } from 'luxon';

/**
 * OwnerDashboard component for displaying the owner's dashboard.
 *
 * Primary parent component which triggers loading the API data on load and when
 * the date range changes.
 *
 * Also handles loading screen, error messages, and displaying the dashboard components.
 *
 * @param {*} param0
 * @returns
 */
const OwnerDashboard = ({ classes, onLoad, path, ...rest }) => {
    const startDate = useSelector(getOwnerDashboardStartDate);
    const endDate = useSelector(getOwnerDashboardEndDate);
    useEffect(() => {
        onLoad();
    }, [onLoad, startDate, endDate]);
    const isLoading = useSelector(getOwnerDashboardLoading);
    const hasError = useSelector(getOwnerDashboardError);
    const properties = useSelector(getOwnersProperties);

    /**
     * Simply checks if there are no properties available and returns a message.
     * This is used to inform the user when they have no properties to view in the dashboard.
     *
     * @returns a message to display when there are no properties available.
     */
    const noPropertyMessage = () => {
        if (
            !isLoading &&
            !hasError &&
            (!properties || properties.length === 0)
        ) {
            return (
                <div className={classes.dashboardErrorContainerStyle}>
                    <Typography className={classes.dashboardErrorTitleStyle}>
                        No properties found.
                    </Typography>
                    <Typography className={classes.dashboardErrorTextStyle}>
                        Contact support to add properties to your account.
                    </Typography>
                </div>
            );
        }
    };

    /**
     * Function to handle rendering the sub component to clean up the main component render.
     * This includes the date range selector and export button.
     *
     * @returns
     */
    const dashboardHeaderControls = () => {
        const start = DateTime.fromMillis(startDate);
        const end = DateTime.fromMillis(endDate);

        const diff = end.diff(start, 'days');
        // if its invalid, set diff to 0
        const diffDays = diff.isValid ? Math.floor(diff.days) : 0;

        return (
            <Grid container alignItems="center">
                <Grid
                    item
                    xs={12}
                    sm={12}
                    md={12}
                    lg={7}
                    className={classes.dashboardHeaderTitleStyle}
                >
                    {diff.isValid && (
                        <Typography variant="h4">
                            {diffDays} Day Rolling Performance Summary
                        </Typography>
                    )}
                    {!diff.isValid && (
                        <Typography variant="h4">
                            Rolling Performance Summary
                        </Typography>
                    )}
                </Grid>
                {!isLoading && (
                    <Grid
                        item
                        xs={12}
                        sm={8}
                        lg={3}
                        className={classes.dashboardHeaderLeftStyle}
                    >
                        <DateRangeDialog />
                    </Grid>
                )}
                {!isLoading && !hasError && (
                    <Grid
                        item
                        xs={12}
                        sm={4}
                        lg={2}
                        className={classes.dashboardHeaderRightStyle}
                    >
                        <ExportChartCSVButton />
                    </Grid>
                )}
            </Grid>
        );
    };

    return (
        <AdminContainer
            path={path}
            isAdmin={false}
            isOwner={true}
            innerHeader
            innerHeaderProps={{
                title: 'Property Profits',
            }}
            {...rest}
        >
            {isLoading && (
                <div className={classes.dashboardLoadingContainerStyle}>
                    <CircularProgress
                        className={classes.dashboardLoadingSpinnerStyle}
                    />
                    <Typography className={classes.dashboardLoadingTextStyle}>
                        Please wait while we load your dashboard data.
                    </Typography>
                </div>
            )}

            {hasError && (
                <div className={classes.dashboardErrorContainerStyle}>
                    <Typography className={classes.dashboardErrorTitleStyle}>
                        Error loading dashboard data.
                    </Typography>
                    <Typography className={classes.dashboardErrorTextStyle}>
                        Please try again later. If the problem persists, contact
                        support.
                    </Typography>
                </div>
            )}

            {noPropertyMessage()}

            {properties.length > 0 && (
                <Grid container direction="column" spacing={8}>
                    {dashboardHeaderControls()}

                    {!isLoading && !hasError && (
                        <>
                            <Hidden mdUp>
                                <Grid item xs={12} sm={12}>
                                    <Grid
                                        item
                                        sm={8}
                                        className={
                                            classes.propertySelectorWrapper
                                        }
                                    >
                                        <PropertySelector />
                                    </Grid>
                                </Grid>
                            </Hidden>
                            <Grid item xs={12}>
                                <OwnerDashboardCards />
                            </Grid>
                            <Hidden smDown>
                                <hr className={classes.dashboardDividerStyle} />
                            </Hidden>
                            <Hidden smDown>
                                <Grid item>
                                    <OwnerDashboardChartView />
                                </Grid>
                            </Hidden>
                        </>
                    )}
                </Grid>
            )}
        </AdminContainer>
    );
};

OwnerDashboard.propTypes = {
    classes: PropTypes.object.isRequired,
    onLoad: PropTypes.func.isRequired,
    path: PropTypes.string,
};

const mapDispatchToProps = function(dispatch) {
    return {
        onLoad: () => dispatch(fetchOwnerDashboardRequested()),
    };
};

export default connect(
    null,
    mapDispatchToProps,
)(withStyles(styles)(OwnerDashboard));
